import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY_PREFIX = 'dochub_column_widths_';
const MIN_COLUMN_WIDTH = 80; // Minimum width for any column
const ACTIONS_COLUMN_WIDTH = 130; // Fixed width for actions column

export const useResizableColumns = (userId, initialColumnConfig) => {
  const storageKey = `${STORAGE_KEY_PREFIX}${userId || 'default'}`;
  const containerRef = useRef(null);
  
  const [columnWidths, setColumnWidths] = useState(() => {
    // Load saved column widths from localStorage
    try {
      const savedWidths = localStorage.getItem(storageKey);
      if (savedWidths) {
        const parsed = JSON.parse(savedWidths);
        // Ensure actions column maintains its width
        if (parsed.actions) {
          parsed.actions = ACTIONS_COLUMN_WIDTH;
        }
        return parsed;
      }
    } catch (error) {
      console.warn('Failed to load saved column widths:', error);
    }
    
    // Create initial widths from config
    const initialWidths = {};
    Object.keys(initialColumnConfig).forEach(key => {
      const width = initialColumnConfig[key].width;
      // Parse width value (remove 'px', 'auto' becomes default)
      if (key === 'actions') {
        initialWidths[key] = ACTIONS_COLUMN_WIDTH;
      } else {
        initialWidths[key] = width === 'auto' ? 200 : parseInt(width) || 120;
      }
    });
    return initialWidths;
  });

  const [isResizing, setIsResizing] = useState(false);
  const [currentColumn, setCurrentColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const resizeRef = useRef(null);

  // Save column widths to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnWidths));
    } catch (error) {
      console.warn('Failed to save column widths:', error);
    }
  }, [columnWidths, storageKey]);

  // Handle mouse move during resize
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      if (!currentColumn) return;
      
      // Don't allow resizing the actions column
      if (currentColumn === 'actions') return;
      
      const diff = e.clientX - startX;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + diff);
      
      // Calculate total width of all columns
      const totalWidth = Object.keys(columnWidths).reduce((sum, key) => {
        if (key === currentColumn) {
          return sum + newWidth;
        }
        return sum + columnWidths[key];
      }, 0);
      
      // Get container width if available
      const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
      
      // Prevent making column so large that other columns would be too small
      const maxWidth = containerWidth - (Object.keys(columnWidths).length - 1) * MIN_COLUMN_WIDTH;
      const finalWidth = Math.min(newWidth, maxWidth);
      
      setColumnWidths(prev => ({
        ...prev,
        [currentColumn]: finalWidth
      }));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setCurrentColumn(null);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, currentColumn, startX, startWidth]);

  // Start resize
  const handleResizeStart = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setCurrentColumn(columnKey);
    setStartX(e.clientX);
    setStartWidth(columnWidths[columnKey] || 120);
    
    // Prevent text selection during resize
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  // Reset column widths to defaults
  const resetColumnWidths = useCallback(() => {
    const defaultWidths = {};
    Object.keys(initialColumnConfig).forEach(key => {
      const width = initialColumnConfig[key].width;
      if (key === 'actions') {
        defaultWidths[key] = ACTIONS_COLUMN_WIDTH;
      } else {
        defaultWidths[key] = width === 'auto' ? 200 : parseInt(width) || 120;
      }
    });
    setColumnWidths(defaultWidths);
  }, [initialColumnConfig]);

  // Get width for a specific column
  const getColumnWidth = useCallback((columnKey) => {
    return columnWidths[columnKey] || 120;
  }, [columnWidths]);

  // Update width for a specific column
  const updateColumnWidth = useCallback((columnKey, width) => {
    // Don't allow updating actions column width
    if (columnKey === 'actions') return;
    
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: Math.max(MIN_COLUMN_WIDTH, width)
    }));
  }, []);

  return {
    columnWidths,
    isResizing,
    handleResizeStart,
    resetColumnWidths,
    getColumnWidth,
    updateColumnWidth,
    containerRef
  };
};